<template>
  <v-content>
    <v-container>
      <v-card>
        <v-tabs v-model="tab" background-color="primary" dark grow>
          <v-tab>
            <v-icon left>record_voice_over</v-icon>
            PQR Externos
          </v-tab>
          <v-tab v-if="puedeMaestros">
            <v-icon left>location_city</v-icon>
            Conjuntos
          </v-tab>
          <v-tab v-if="puedeMaestros">
            <v-icon left>account_tree</v-icon>
            Dependencias
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Bandeja de PQR externos -->
          <v-tab-item>
            <v-card-title>
              <v-chip class="mr-2" color="primary" dark>{{ filtered.length }} registros</v-chip>
              <v-chip v-if="vencidosCount > 0" class="mr-2" color="error" dark>
                {{ vencidosCount }} vencidos
              </v-chip>
              <v-chip
                v-if="filterReporteId"
                class="mr-2"
                color="indigo"
                dark
                close
                @click:close="filterReporteId = null"
              >
                Viendo un solo reporte
              </v-chip>
              <v-spacer/>
              <download-excel
                v-if="filtered.length > 0"
                class="v-btn v-btn--contained theme--light v-size--default success"
                :data="excelData"
                :fields="json_fields"
                worksheet="PQR Externos"
                :name="`pqr_externos_${$moment().format('YYYY-MM-DD')}.xls`"
              >
                Exportar excel
              </download-excel>
            </v-card-title>

            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filterTipo"
                    :items="tipoOptions"
                    item-text="label"
                    item-value="value"
                    label="Tipo"
                    clearable
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filterEstado"
                    :items="estadoOptions"
                    item-text="label"
                    item-value="value"
                    label="Estado"
                    clearable
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filterConjunto"
                    :items="conjuntoNombres"
                    label="Conjunto"
                    clearable
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="search"
                    append-icon="search"
                    label="Buscar"
                    single-line
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
            </v-card-text>

            <v-data-table
              :headers="headers"
              :items="filtered"
              sort-by="createdAtMs"
              sort-desc
              class="elevation-1"
              :items-per-page="10"
              no-data-text="No hay PQR externos registrados"
              no-results-text="No se encontraron resultados"
            >
              <template v-slot:item.createdAtMs="{ item }">
                {{ item.createdAtMs ? $moment(item.createdAtMs).format('DD/MM/YYYY HH:mm') : '-' }}
              </template>

              <template v-slot:item.tipo="{ item }">
                <v-chip :color="tipoColor(item.tipo)" dark small>{{ tipoLabel(item.tipo) }}</v-chip>
              </template>

              <template v-slot:item.conjuntoNombre="{ item }">
                {{ item.conjuntoNombre }}
                <v-chip
                  v-if="item.reporteTotal > 1"
                  x-small
                  color="indigo lighten-4"
                  class="ml-1"
                  title="Forma parte de un reporte con varias novedades"
                  @click.stop="verReporte(item)"
                >
                  {{ item.reporteIndice }}/{{ item.reporteTotal }}
                </v-chip>
              </template>

              <template v-slot:item.estado="{ item }">
                <v-chip :color="estadoColor(item.estado)" dark small>{{ estadoLabel(item.estado) }}</v-chip>
                <v-chip
                  v-if="item.seguimientos.length > 0"
                  x-small
                  class="ml-1"
                  title="Gestiones registradas"
                >
                  <v-icon x-small left>history</v-icon>
                  {{ item.seguimientos.length }}
                </v-chip>
                <v-chip
                  v-if="cantidadAdjuntos(item) > 0"
                  x-small
                  class="ml-1"
                  color="indigo lighten-5"
                  title="Soportes adjuntos"
                >
                  <v-icon x-small left>attach_file</v-icon>
                  {{ cantidadAdjuntos(item) }}
                </v-chip>
                <v-chip
                  v-if="item.esperandoCierre && !estaCerrado(item)"
                  x-small
                  class="ml-1"
                  color="amber darken-3"
                  dark
                  title="La dependencia ya lo gestiono, falta el cierre"
                >
                  Esperando cierre
                </v-chip>
                <v-chip
                  v-if="cerradoSinSoporte(item)"
                  x-small
                  class="ml-1"
                  color="orange darken-2"
                  dark
                  title="Se cerro sin adjuntar ningun comprobante"
                >
                  Sin soporte
                </v-chip>
              </template>

              <template v-slot:item.terminos="{ item }">
                <v-chip :color="terminoColor(item)" dark small>{{ terminoLabel(item) }}</v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click="openDetail(item)">
                  <v-icon small>visibility</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Maestros: alimentan los desplegables de la app movil -->
          <v-tab-item v-if="puedeMaestros">
            <MaestrosPqr tipo="conjunto"/>
          </v-tab-item>

          <v-tab-item v-if="puedeMaestros">
            <MaestrosPqr tipo="dependencia"/>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- Detalle y gestion -->
      <v-dialog v-model="dialog" max-width="720" scrollable>
        <v-card v-if="selected">
          <v-card-title :class="`${tipoColor(selected.tipo)} white--text`">
            <v-icon dark class="mr-2">record_voice_over</v-icon>
            {{ tipoLabel(selected.tipo) }}
            <v-spacer/>
            <v-chip :color="terminoColor(selected)" dark small>{{ terminoLabel(selected) }}</v-chip>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-alert
              v-if="selected.reporteTotal > 1"
              type="info"
              dense
              text
              class="mb-3"
            >
              Novedad {{ selected.reporteIndice }} de {{ selected.reporteTotal }} de un mismo reporte.
              Cada una se responde por separado.
              <a href="#" class="ml-1" @click.prevent="verReporte(selected)">Ver las {{ selected.reporteTotal }}</a>
            </v-alert>

            <v-list dense>
              <v-list-item>
                <v-list-item-icon><v-icon>person</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Reportado por</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ selected.user ? (selected.user.first_name || '') + ' ' + (selected.user.last_name || '') : 'Sin usuario' }}
                    <span v-if="selected.user && selected.user.role" class="caption grey--text">
                      ({{ selected.user.role }})
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon><v-icon>location_city</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Cliente / conjunto</v-list-item-subtitle>
                  <v-list-item-title>{{ selected.conjuntoNombre || 'Sin conjunto' }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item v-if="selected.contactoCliente">
                <v-list-item-icon><v-icon>contact_phone</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Contacto del cliente</v-list-item-subtitle>
                  <v-list-item-title>{{ selected.contactoCliente }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon><v-icon>account_tree</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Dirigido a</v-list-item-subtitle>
                  <v-list-item-title>{{ selected.dependenciaNombre || 'Sin dependencia' }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon><v-icon>event</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Fecha de registro</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ selected.createdAtMs ? $moment(selected.createdAtMs).format('dddd D [de] MMMM [de] YYYY, HH:mm') : '-' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <div class="subtitle-2 grey--text mb-1 mt-3">Descripcion de la novedad</div>
            <div class="body-1 mb-4" style="white-space: pre-wrap">{{ selected.descripcion }}</div>

            <v-divider class="mb-4"/>

            <!-- Respuesta oficial vigente: es lo unico que ve quien cargo el PQR -->
            <template v-if="selected.respuesta">
              <div class="subtitle-2 grey--text mb-1">Respuesta oficial al cliente</div>
              <v-alert type="success" dense text class="mb-1" style="white-space: pre-wrap">
                {{ selected.respuesta }}
              </v-alert>
              <div v-if="selected.respondidoPor" class="caption grey--text mb-4">
                Por {{ selected.respondidoPor.email }}
                <span v-if="selected.respondidoAtMs">
                  el {{ $moment(selected.respondidoAtMs).format('DD/MM/YYYY HH:mm') }}
                </span>
              </div>
            </template>

            <!-- Bitacora -->
            <div class="subtitle-1 mb-2">
              Historial de seguimiento
              <v-chip x-small class="ml-1">{{ seguimientos.length }}</v-chip>
            </div>

            <div v-if="seguimientos.length === 0" class="caption grey--text mb-4">
              Todavia no hay gestiones registradas para este PQR.
            </div>

            <v-timeline v-else dense class="mb-4 pt-0">
              <v-timeline-item
                v-for="(s, i) in seguimientos"
                :key="i"
                small
                :color="estadoColor(s.estadoNuevo)"
              >
                <div class="caption grey--text">
                  {{ s.fechaMs ? $moment(s.fechaMs).format('DD/MM/YYYY HH:mm') : '' }}
                  <span v-if="s.autor"> · {{ s.autor.email }}</span>
                </div>

                <div v-if="s.estadoAnterior !== s.estadoNuevo" class="body-2">
                  <v-chip x-small :color="estadoColor(s.estadoAnterior)" dark>
                    {{ estadoLabel(s.estadoAnterior) }}
                  </v-chip>
                  <v-icon x-small class="mx-1">arrow_forward</v-icon>
                  <v-chip x-small :color="estadoColor(s.estadoNuevo)" dark>
                    {{ estadoLabel(s.estadoNuevo) }}
                  </v-chip>
                </div>

                <div v-if="s.nota" class="body-2 mt-1" style="white-space: pre-wrap">
                  {{ s.nota }}
                  <v-chip v-if="s.oficial" x-small color="success" dark class="ml-1">
                    Respuesta al cliente
                  </v-chip>
                </div>

                <!-- Soportes de esta gestion -->
                <div class="mt-1">
                  <v-chip
                    v-for="(a, j) in (s.adjuntos || [])"
                    :key="j"
                    x-small
                    class="mr-1 mb-1"
                    color="indigo lighten-5"
                    style="cursor: pointer"
                    @click="abrirAdjunto(a)"
                  >
                    <v-icon x-small left>{{ iconoAdjunto(a) }}</v-icon>
                    {{ a.nombre }}
                    <span class="grey--text ml-1">{{ tamanoLegible(a.tamano) }}</span>
                  </v-chip>

                  <span
                    v-if="!s.adjuntos || s.adjuntos.length === 0"
                    class="caption grey--text text--lighten-1"
                  >
                    Sin soporte adjunto
                  </span>
                </div>
              </v-timeline-item>
            </v-timeline>

            <v-divider class="mb-4"/>

            <v-alert
              v-if="selected.esperandoCierre && !estaCerrado(selected)"
              type="warning"
              dense
              text
              class="mb-3"
            >
              La dependencia marco este PQR como gestionado. Falta que un perfil de
              control revise la respuesta y cierre el caso.
            </v-alert>

            <div class="subtitle-1 mb-3">Nueva gestion</div>

            <v-select
              :disabled="!puedeCambiarEstado"
              :hint="puedeCambiarEstado ? '' : 'Tu perfil no puede cambiar el estado. Al terminar, marca el PQR como gestionado.'"
              :persistent-hint="!puedeCambiarEstado"
              v-model="gestion.estado"
              :items="estadoOptions"
              item-text="label"
              item-value="value"
              label="Estado"
              outlined
              dense
            />

            <v-textarea
              v-model="gestion.nota"
              :disabled="!puedeGestionar && !puedeCambiarEstado"
              @input="alCambiarNota"
              label="Nota de seguimiento"
              placeholder="Que se hizo, con quien se hablo, que falta..."
              outlined
              rows="4"
              auto-grow
            />

            <v-file-input
              v-model="gestion.archivos"
              :accept="acceptAdjuntos"
              :disabled="!puedeGestionar && !puedeCambiarEstado"
              label="Soporte (opcional)"
              placeholder="Adjuntá el comprobante de lo que se hizo"
              hint="Imagen, PDF, Word, Excel o correo. Hasta 10 MB por archivo."
              persistent-hint
              multiple
              chips
              small-chips
              show-size
              outlined
              dense
              prepend-icon="attach_file"
              class="mb-2"
            />

            <v-checkbox
              v-model="gestion.oficial"
              :disabled="!puedeResponder || !gestion.nota || !gestion.nota.trim()"
              class="mt-0"
              hide-details
            >
              <template v-slot:label>
                <div>
                  Esta nota es la respuesta oficial al cliente
                  <div class="caption grey--text">
                    Si no la marcas, queda como nota interna y no se ve desde la app.
                  </div>
                </div>
              </template>
            </v-checkbox>

            <v-alert v-if="requiereRespuesta" type="warning" dense text class="mt-3">
              Para marcar el PQR como Resuelto o Cerrado tiene que haber una respuesta oficial.
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>
            <v-btn
              v-if="puedeMarcarGestionado && !selected.esperandoCierre && !estaCerrado(selected)"
              color="orange darken-2"
              text
              :disabled="saving"
              @click="marcarGestionado"
            >
              <v-icon left small>task_alt</v-icon>
              Marcar como gestionado
            </v-btn>
            <v-btn text @click="dialog = false">Cerrar</v-btn>
            <v-btn
              color="primary"
              :disabled="!hayCambios || requiereRespuesta || saving"
              :loading="saving"
              @click="guardarGestion"
            >
              {{ subiendo ? 'Subiendo soporte...' : 'Guardar gestion' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Visor de soportes -->
      <v-dialog v-model="visor.abierto" max-width="1000" scrollable>
        <v-card v-if="visor.adjunto" class="visor-card">
          <v-card-title class="py-2">
            <v-icon class="mr-2">{{ iconoAdjunto(visor.adjunto) }}</v-icon>
            <span class="subtitle-1 text-truncate">{{ visor.adjunto.nombre }}</span>
            <v-spacer/>
            <v-btn icon :href="visor.adjunto.url" target="_blank" rel="noopener" title="Descargar">
              <v-icon>file_download</v-icon>
            </v-btn>
            <v-btn icon @click="visor.abierto = false" title="Cerrar">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider/>

          <!-- Los `v-if` incluyen `visor.abierto` porque v-dialog no destruye su
               contenido al cerrarse, solo lo oculta: sin esto el video seguia
               sonando de fondo y el PDF quedaba cargado. -->
          <v-card-text class="visor-contenido">
            <img
              v-if="visor.abierto && visor.tipo === 'imagen'"
              :src="visor.adjunto.url"
              :alt="visor.adjunto.nombre"
              class="visor-media"
            />

            <video
              v-else-if="visor.abierto && visor.tipo === 'video'"
              :src="visor.adjunto.url"
              class="visor-media"
              controls
              autoplay
            />

            <iframe
              v-else-if="visor.abierto && visor.tipo === 'pdf'"
              :src="visor.adjunto.url"
              class="visor-pdf"
              frameborder="0"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-snackbar :color="snackColor" v-model="snackbar">
        {{ text }}
        <v-btn color="white" text @click="snackbar = false">Cerrar</v-btn>
      </v-snackbar>
    </v-container>
  </v-content>
</template>

<script>
import { mapState } from 'vuex'
import MaestrosPqr from '../components/MaestrosPqr'
import { diasHabilesEntre } from '../plugins/festivos'
import {
  ACCEPT_ATTR,
  validarLote,
  subirAdjuntos,
  borrarAdjuntos,
  iconoAdjunto,
  tamanoLegible,
  tipoVisor
} from '../plugins/adjuntos'

// Termino legal para responder un PQR en Colombia: 15 dias habiles.
const DIAS_TERMINO = 15

// Memo de dias habiles. El calculo recorre el calendario dia por dia y la tabla
// lo pide dos veces por fila en cada re-render (por ejemplo, al tipear en el
// buscador). Se cachea por PQR y se descarta cuando cambia el dia. Va fuera de
// `data()` a proposito: es cache, no estado reactivo.
const cacheDiasHabiles = { dia: '', valores: {} }

export default {
  name: 'PqrsExternos',

  components: { MaestrosPqr },

  data() {
    return {
      tab: 0,
      search: '',
      filterTipo: null,
      filterEstado: null,
      filterConjunto: null,
      filterReporteId: null,
      dialog: false,
      selectedId: null,
      saving: false,
      subiendo: false,
      visor: { abierto: false, adjunto: null, tipo: '' },
      snackbar: false,
      snackColor: 'success',
      text: '',
      gestion: {
        estado: 'NUEVO',
        nota: '',
        oficial: false,
        archivos: []
      },
      acceptAdjuntos: ACCEPT_ATTR,
      // El valor guardado va sin tildes; la etiqueta es lo que ve el usuario.
      tipoOptions: [
        { value: 'Peticion', label: 'Petición' },
        { value: 'Queja', label: 'Queja' },
        { value: 'Reclamo', label: 'Reclamo' },
        { value: 'Sugerencia', label: 'Sugerencia' }
      ],
      estadoOptions: [
        { value: 'NUEVO', label: 'Nuevo' },
        { value: 'EN_REVISION', label: 'En revisión' },
        { value: 'RESUELTO', label: 'Resuelto' },
        { value: 'CERRADO', label: 'Cerrado' }
      ],
      headers: [
        { text: 'Fecha', value: 'createdAtMs' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Reportado por', value: 'reportanteTexto' },
        { text: 'Cliente / conjunto', value: 'conjuntoNombre' },
        { text: 'Dirigido a', value: 'dependenciaNombre' },
        { text: 'Estado', value: 'estado' },
        { text: 'Terminos', value: 'terminos', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      json_fields: {
        Fecha: 'fechaTexto',
        Tipo: 'tipoTexto',
        'Reportado por': 'reportanteTexto',
        Cliente: 'conjuntoNombre',
        'Contacto del cliente': 'contactoCliente',
        'Dirigido a': 'dependenciaNombre',
        Descripcion: 'descripcion',
        Estado: 'estadoTexto',
        'Respuesta oficial': 'respuesta',
        Gestiones: 'cantidadGestiones',
        'Soportes adjuntos': 'cantidadSoportes',
        'Historial de seguimiento': 'historialTexto',
        Reporte: 'reporteTexto',
        'Dias habiles transcurridos': 'diasHabiles'
      }
    }
  },

  computed: {
    ...mapState({
      pqrsExternos: state => state.pqrsExterno.pqrsExternos,
      conjuntos: state => state.conjunto.conjuntos
    }),

    // Normaliza los timestamps de Firestore a milisegundos para poder ordenar y formatear.
    items() {
      return this.pqrsExternos.map(p => ({
        ...p,
        // vuexfire expone `id` como propiedad NO enumerable, asi que el spread
        // de arriba la descarta. Hay que copiarla a mano o se pierde el doc id.
        id: p.id,
        estado: p.estado || 'NUEVO',
        // Los PQR anteriores a la bitacora no traen el campo.
        seguimientos: Array.isArray(p.seguimientos) ? p.seguimientos : [],
        reporteTotal: p.reporteTotal || 1,
        reporteIndice: p.reporteIndice || 1,
        reportanteTexto: p.user
          ? `${p.user.first_name || ''} ${p.user.last_name || ''}`.trim()
          : 'Sin usuario',
        createdAtMs: this.toMillis(p.createdAt),
        respondidoAtMs: this.toMillis(p.respondidoAt)
      }))
    },

    // Todo lo que este usuario tiene derecho a ver, sin los filtros de pantalla.
    // Defensa en profundidad: la consulta ya viene acotada por dependencia,
    // pero cuando el gestor tiene mas de diez dependencias la consulta trae la
    // coleccion entera (limite del operador `in`), asi que el recorte real pasa
    // por aca. Todo lo que se cuente, liste o exporte sale de este computed y
    // no de `items`, o se filtrarian los PQR ajenos en la tabla pero no en el
    // contador de vencidos ni en el desplegable de conjuntos.
    visibles() {
      if (this.puedeVerTodas) return this.items
      const mias = this.misDependencias
      return this.items.filter(p => mias.includes(p.dependenciaId))
    },

    // Se filtra aca -y no con la prop `search` de v-data-table- para que el
    // contador, la exportacion a Excel y la tabla muestren siempre lo mismo,
    // y para poder buscar dentro de la descripcion y la respuesta.
    filtered() {
      const q = (this.search || '').trim().toLowerCase()

      return this.visibles.filter(p => {
        if (this.filterReporteId && p.reporteId !== this.filterReporteId) return false
        if (this.filterTipo && p.tipo !== this.filterTipo) return false
        if (this.filterEstado && p.estado !== this.filterEstado) return false
        if (this.filterConjunto && p.conjuntoNombre !== this.filterConjunto) return false
        if (!q) return true

        return [
          p.reportanteTexto,
          p.conjuntoNombre,
          p.dependenciaNombre,
          p.contactoCliente,
          p.descripcion,
          p.respuesta,
          this.tipoLabel(p.tipo),
          this.estadoLabel(p.estado)
        ].some(v => (v || '').toString().toLowerCase().includes(q))
      })
    },

    conjuntoNombres() {
      const desdeMaestro = this.conjuntos.map(c => c.name)
      const desdeRegistros = this.visibles.map(p => p.conjuntoNombre).filter(Boolean)
      return [...new Set([...desdeMaestro, ...desdeRegistros])].sort()
    },

    // Se resuelve contra la lista en vez de guardar una copia, asi el detalle
    // refleja lo que otro administrador haya guardado mientras esta abierto.
    selected() {
      if (!this.selectedId) return null
      return this.visibles.find(p => p.id === this.selectedId) || null
    },

    // Acompania al contador de registros, asi que responde a los mismos filtros:
    // si dijera "3 vencidos" sobre una tabla de 1 fila no se entenderia de donde salen.
    vencidosCount() {
      return this.filtered.filter(p => this.estaVencido(p)).length
    },

    // Se exporta exactamente lo que quedo en pantalla: `filtered`, no `items`.
    excelData() {
      return this.filtered.map(p => ({
        ...p,
        fechaTexto: p.createdAtMs ? this.$moment(p.createdAtMs).format('DD/MM/YYYY HH:mm') : '',
        tipoTexto: this.tipoLabel(p.tipo),
        estadoTexto: this.estadoLabel(p.estado),
        reportanteTexto: this.escaparExcel(p.reportanteTexto),
        conjuntoNombre: this.escaparExcel(p.conjuntoNombre),
        dependenciaNombre: this.escaparExcel(p.dependenciaNombre),
        descripcion: this.escaparExcel(p.descripcion),
        contactoCliente: this.escaparExcel(p.contactoCliente),
        reporteTexto: p.reporteTotal > 1 ? `${p.reporteIndice} de ${p.reporteTotal}` : 'Unica',
        cantidadGestiones: p.seguimientos.length,
        cantidadSoportes: this.cantidadAdjuntos(p),
        historialTexto: this.escaparExcel(this.historialATexto(p.seguimientos)),
        respuesta: this.escaparExcel(p.respuesta),
        diasHabiles: this.diasHabilesTranscurridos(p)
      }))
    },

    puedeMaestros() {
      return this.$store.getters.puede('maestros.administrar')
    },

    puedeVerTodas() {
      return this.$store.getters.puede('pqr.verTodas')
    },

    puedeCambiarEstado() {
      return this.$store.getters.puede('pqr.cambiarEstado')
    },

    puedeResponder() {
      return this.$store.getters.puede('pqr.responder')
    },

    puedeGestionar() {
      return this.$store.getters.puede('pqr.gestionar')
    },

    puedeMarcarGestionado() {
      return this.$store.getters.puede('pqr.marcarGestionado')
    },

    misDependencias() {
      return this.$store.getters.misDependencias
    },

    // Bitacora del PQR abierto, mas reciente primero.
    seguimientos() {
      if (!this.selected || !Array.isArray(this.selected.seguimientos)) return []
      return this.selected.seguimientos
        .map(s => ({ ...s, fechaMs: this.toMillis(s.fecha) }))
        .sort((a, b) => (b.fechaMs || 0) - (a.fechaMs || 0))
    },

    // Cerrar un caso sin ninguna respuesta oficial no aporta trazabilidad.
    requiereRespuesta() {
      if (!this.selected || !this.puedeCambiarEstado) return false

      // Solo aplica si en esta gestion se esta pasando el caso a cerrado. Un PQR
      // que ya estaba cerrado sin respuesta no debe quedar trabado para siempre.
      const cierraAhora = (this.gestion.estado === 'RESUELTO' || this.gestion.estado === 'CERRADO')
        && this.gestion.estado !== this.selected.estado
      if (!cierraAhora) return false

      const yaTieneRespuesta = !!(this.selected && (this.selected.respuesta || '').trim())
      const laDaAhora = this.gestion.oficial && !!(this.gestion.nota || '').trim()
      return !(yaTieneRespuesta || laDaAhora)
    },

    // Evita registrar entradas vacias en la bitacora.
    hayCambios() {
      if (!this.selected) return false
      const cambioEstado = this.puedeCambiarEstado && this.gestion.estado !== this.selected.estado
      const hayNota = !!(this.gestion.nota || '').trim()
      const hayArchivos = (this.gestion.archivos || []).length > 0
      return cambioEstado || hayNota || hayArchivos
    }
  },

  mounted() {
    // El alcance viene resuelto en la sesion, asi que la consulta se puede
    // armar de entrada sin esperar a que cargue ninguna coleccion.
    this.suscribirPqrs()
    this.$store.dispatch('conjunto/bindConjuntos')
    this.$store.dispatch('dependencia/bindDependencias')
  },

  methods: {
    // Los perfiles sin permiso de ver todo solo reciben los PQR de sus
    // dependencias: el filtro va en la consulta, no en la pantalla.
    suscribirPqrs() {
      const opciones = this.puedeVerTodas
        ? { todas: true }
        : { dependencias: this.misDependencias }
      return this.$store.dispatch('pqrsExterno/bindPqrsExternos', opciones)
        .catch(() => {
          this.notify('No se pudieron cargar los PQR. Revisá tus permisos o la conexión.', 'error')
        })
    },

    async marcarGestionado() {
      if (this.saving || !this.selected) return
      if (!this.puedeMarcarGestionado) {
        this.notify('Tu perfil no puede marcar PQR como gestionados', 'error')
        return
      }
      // Marcar como gestionado un caso ya resuelto lo dejaria "esperando cierre"
      // de un cierre que ya paso, y ese cartel no se apaga solo.
      if (this.estaCerrado(this.selected)) {
        this.notify('Este PQR ya fue cerrado: no hace falta marcarlo como gestionado', 'error')
        return
      }
      if (this.selected.esperandoCierre) return

      const archivos = this.gestion.archivos || []

      // Se valida todo el lote antes de subir nada, para no dejar archivos
      // huerfanos en Storage si uno no cumple.
      const errorLote = validarLote(archivos)
      if (errorLote) {
        this.notify(errorLote, 'error')
        return
      }

      this.saving = true

      // Se congelan id y estado antes del primer await: `selected` es un computed
      // sobre la coleccion en vivo y puede cambiar -o quedar en null- mientras
      // se sube un soporte.
      const id = this.selected.id
      const estadoActual = this.selected.estado
      const admin = this.$store.getters.user
      const autor = admin ? { id: admin.id, email: admin.email } : null
      const nota = (this.gestion.nota || '').trim()
      const esOficial = this.puedeResponder && this.gestion.oficial && !!nota
      const fecha = new Date()

      let adjuntos = []

      try {
        if (archivos.length > 0) {
          this.subiendo = true
          adjuntos = await subirAdjuntos(id, archivos)
          this.subiendo = false
        }

        const entrada = {
          fecha,
          autor,
          estadoAnterior: estadoActual,
          estadoNuevo: estadoActual,
          nota: nota || 'Gestion terminada, queda a la espera del cierre.',
          oficial: esOficial,
          gestionado: true,
          adjuntos
        }

        const data = { esperandoCierre: true }
        if (esOficial) {
          data.respuesta = nota
          data.respuestaAdjuntos = adjuntos
          data.respondidoPor = autor
          data.respondidoAt = fecha
        }

        await this.$store.dispatch('pqrsExterno/agregarSeguimiento', { id, entrada, data })

        this.notify('Marcado como gestionado. Queda esperando el cierre.', 'success')
        this.gestion = { estado: this.gestion.estado, nota: '', oficial: false, archivos: [] }
      } catch (e) {
        // Si los soportes subieron pero la escritura fallo, quedarian en Storage
        // sin ninguna entrada de bitacora que los referencie.
        if (adjuntos.length > 0) await borrarAdjuntos(adjuntos)
        this.notify(
          this.subiendo
            ? 'Error al subir el soporte. Revisá el archivo y los permisos de Storage.'
            : 'No se pudo marcar como gestionado',
          'error'
        )
      } finally {
        this.subiendo = false
        this.saving = false
      }
    },

    iconoAdjunto,
    tamanoLegible,

    // Imagenes, videos y PDF se ven dentro del panel. Word, Excel y correos
    // no tienen visor posible en el navegador, asi que se descargan.
    abrirAdjunto(adjunto) {
      const tipo = tipoVisor(adjunto)
      if (tipo === 'descarga') {
        window.open(adjunto.url, '_blank', 'noopener')
        return
      }
      this.visor = { abierto: true, adjunto, tipo }
    },

    // Total de soportes cargados en toda la bitacora del PQR.
    cantidadAdjuntos(item) {
      return (item.seguimientos || []).reduce(
        (total, s) => total + ((s.adjuntos && s.adjuntos.length) || 0),
        0
      )
    },

    // Un caso cerrado sin ningun soporte es lo que hay que poder detectar:
    // alguien escribio "enviado" y cerro sin adjuntar el comprobante.
    cerradoSinSoporte(item) {
      return this.estaCerrado(item) && this.cantidadAdjuntos(item) === 0
    },

    toMillis(value) {
      if (!value) return null
      if (typeof value.toDate === 'function') return value.toDate().getTime()
      if (value instanceof Date) return value.getTime()
      if (value.seconds) return value.seconds * 1000
      return null
    },

    // Dias habiles transcurridos, descontando fines de semana y festivos colombianos.
    diasHabilesTranscurridos(item) {
      if (!item || !item.createdAtMs) return 0

      const hoy = new Date()
      const dia = `${hoy.getFullYear()}-${hoy.getMonth()}-${hoy.getDate()}`
      if (cacheDiasHabiles.dia !== dia) {
        cacheDiasHabiles.dia = dia
        cacheDiasHabiles.valores = {}
      }

      if (cacheDiasHabiles.valores[item.createdAtMs] === undefined) {
        cacheDiasHabiles.valores[item.createdAtMs] =
          diasHabilesEntre(new Date(item.createdAtMs), hoy)
      }
      return cacheDiasHabiles.valores[item.createdAtMs]
    },

    estaCerrado(item) {
      return item.estado === 'RESUELTO' || item.estado === 'CERRADO'
    },

    estaVencido(item) {
      return !this.estaCerrado(item) && this.diasHabilesTranscurridos(item) > DIAS_TERMINO
    },

    terminoLabel(item) {
      if (this.estaCerrado(item)) return 'Atendido'
      const dias = this.diasHabilesTranscurridos(item)
      if (dias > DIAS_TERMINO) return `Vencido (${dias} dias)`
      return `${dias} de ${DIAS_TERMINO} dias`
    },

    terminoColor(item) {
      if (this.estaCerrado(item)) return 'grey'
      const dias = this.diasHabilesTranscurridos(item)
      if (dias > DIAS_TERMINO) return 'red darken-2'
      if (dias >= 10) return 'orange darken-2'
      return 'green'
    },

    tipoLabel(tipo) {
      const found = this.tipoOptions.find(t => t.value === tipo)
      return found ? found.label : (tipo || '-')
    },

    tipoColor(tipo) {
      const colors = {
        Peticion: 'blue',
        Petición: 'blue',
        Queja: 'red',
        Reclamo: 'purple',
        Sugerencia: 'green'
      }
      return colors[tipo] || 'grey'
    },

    // El .xls que genera vue-json-excel es en realidad una tabla HTML y la
    // libreria mete los valores tal cual, sin escapar. Una descripcion con
    // "<", ">" o "&" -por ejemplo un correo escrito como <juan@cliente.com>-
    // rompe la tabla y el archivo se abre incompleto o directamente falla.
    // Los saltos de linea NO se tocan: la libreria los convierte despues en <br/>.
    escaparExcel(valor) {
      if (valor === null || valor === undefined) return ''
      return valor
        .toString()
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },

    // Aplana la bitacora para que entre en una celda del Excel.
    historialATexto(seguimientos) {
      if (!seguimientos || seguimientos.length === 0) return ''
      return seguimientos
        .map(s => ({ ...s, ms: this.toMillis(s.fecha) }))
        .sort((a, b) => (a.ms || 0) - (b.ms || 0))
        .map(s => {
          const fecha = s.ms ? this.$moment(s.ms).format('DD/MM/YYYY HH:mm') : ''
          const autor = s.autor ? s.autor.email : 'sin autor'
          const cambio = s.estadoAnterior !== s.estadoNuevo
            ? ` [${this.estadoLabel(s.estadoAnterior)} -> ${this.estadoLabel(s.estadoNuevo)}]`
            : ''
          const marca = s.oficial ? ' (respuesta al cliente)' : ''
          const soportes = (s.adjuntos && s.adjuntos.length)
            ? ` | Soportes: ${s.adjuntos.map(a => a.nombre).join(', ')}`
            : ' | Sin soporte'
          return `${fecha} - ${autor}${cambio}${marca}: ${s.nota || 'sin nota'}${soportes}`
        })
        .join('\n')
    },

    estadoLabel(estado) {
      const found = this.estadoOptions.find(e => e.value === estado)
      return found ? found.label : 'Nuevo'
    },

    estadoColor(estado) {
      const colors = {
        NUEVO: 'blue-grey',
        EN_REVISION: 'orange darken-2',
        RESUELTO: 'green',
        CERRADO: 'grey darken-1'
      }
      return colors[estado] || 'blue-grey'
    },

    // Aisla en la tabla las novedades que llegaron en el mismo envio.
    verReporte(item) {
      // Los PQR viejos no tienen `reporteId`. Sin la guarda se limpiarian todos
      // los filtros para despues no filtrar por nada.
      if (!item || !item.reporteId) {
        this.notify('Este PQR no tiene un reporte asociado', 'error')
        return
      }
      this.filterReporteId = item.reporteId
      this.filterTipo = null
      this.filterEstado = null
      this.filterConjunto = null
      this.search = ''
      this.dialog = false
    },

    // Si la nota queda vacia, la marca de "respuesta oficial" pierde sentido
    // y no puede quedar pegada para la siguiente nota que se escriba.
    alCambiarNota() {
      if (!(this.gestion.nota || '').trim()) {
        this.gestion.oficial = false
      }
    },

    openDetail(item) {
      this.selectedId = item.id
      // La nota arranca vacia: cada guardado agrega una entrada nueva a la
      // bitacora, no edita la anterior.
      this.gestion = {
        estado: item.estado || 'NUEVO',
        nota: '',
        oficial: false,
        archivos: []
      }
      this.dialog = true
    },

    async guardarGestion() {
      if (this.saving || !this.hayCambios || this.requiereRespuesta) return
      if (!this.puedeGestionar && !this.puedeCambiarEstado) {
        this.notify('Tu perfil no puede registrar gestiones en este PQR', 'error')
        return
      }

      const archivos = this.gestion.archivos || []

      // Se valida todo el lote antes de subir nada, para no dejar archivos
      // huerfanos en Storage si uno no cumple.
      const errorLote = validarLote(archivos)
      if (errorLote) {
        this.notify(errorLote, 'error')
        return
      }

      this.saving = true

      // Se congelan id y estado antes del primer await: `selected` es un computed
      // sobre la coleccion en vivo y puede cambiar -o quedar en null- mientras
      // se sube un soporte.
      const id = this.selected.id
      const estadoActual = this.selected.estado
      const esperabaCierre = this.selected.esperandoCierre === true
      const admin = this.$store.getters.user
      const autor = admin ? { id: admin.id, email: admin.email } : null
      const nota = (this.gestion.nota || '').trim()
      const esOficial = this.puedeResponder && this.gestion.oficial && !!nota
      const fecha = new Date()
      const estadoDestino = this.puedeCambiarEstado ? this.gestion.estado : estadoActual
      const cambioDeEstado = estadoDestino !== estadoActual

      let adjuntos = []

      try {
        if (archivos.length > 0) {
          this.subiendo = true
          adjuntos = await subirAdjuntos(id, archivos)
          this.subiendo = false
        }

        const entrada = {
          fecha,
          autor,
          estadoAnterior: estadoActual,
          estadoNuevo: estadoDestino,
          nota,
          oficial: esOficial,
          gestionado: false,
          adjuntos
        }

        const data = {}

        // El estado solo viaja si el perfil puede cambiarlo. Asi una gestion no
        // revierte sin querer un cierre hecho por el auditor en paralelo.
        if (this.puedeCambiarEstado) {
          data.estado = estadoDestino
        }

        // Que el control registre una gestion cierra la espera, cambie o no el
        // estado. Antes solo se limpiaba al cambiar de estado, y eso dejaba el
        // PQR trabado: si el auditor lo devolvia con una nota sin mover el
        // estado, `esperandoCierre` quedaba en true para siempre y el gestor ya
        // no veia el boton "Marcar como gestionado" para volver a avisar.
        if (this.puedeCambiarEstado && (cambioDeEstado || esperabaCierre)) {
          data.esperandoCierre = false
        }

        // `respuesta` y `respuestaAdjuntos` son lo unico que ve quien cargo el
        // PQR desde la app, asi que solo se actualizan con la respuesta oficial.
        if (esOficial) {
          data.respuesta = nota
          data.respuestaAdjuntos = adjuntos
          data.respondidoPor = autor
          data.respondidoAt = fecha
        }

        await this.$store.dispatch('pqrsExterno/agregarSeguimiento', { id, entrada, data })

        this.notify('Gestion registrada correctamente', 'success')
        // El dialogo queda abierto para ver la entrada recien agregada en la bitacora.
        this.gestion = {
          estado: estadoDestino,
          nota: '',
          oficial: false,
          archivos: []
        }
      } catch (e) {
        // Si los soportes subieron pero la escritura fallo, quedarian en Storage
        // sin ninguna entrada de bitacora que los referencie.
        if (adjuntos.length > 0) await borrarAdjuntos(adjuntos)
        this.notify(
          this.subiendo
            ? 'Error al subir el soporte. Revisá el archivo y los permisos de Storage.'
            : 'Error al registrar la gestion',
          'error'
        )
      } finally {
        this.subiendo = false
        this.saving = false
      }
    },

    notify(message, color) {
      this.text = message
      this.snackColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.visor-contenido {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1e1e1e;
  padding: 12px;
  min-height: 320px;
}

.visor-media {
  max-width: 100%;
  max-height: 72vh;
  object-fit: contain;
}

.visor-pdf {
  width: 100%;
  height: 72vh;
  background: #fff;
}
</style>
